import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, MessageCircle, Minus, Plus, Trash2 } from "lucide-react";
import { api, errMsg, imgUrl } from "@/lib/api";
import { rp, waContacts } from "@/lib/format";
import { useSeo, useSettings, useShop, lineMax } from "@/lib/shop";
import { Empty, Loading } from "@/components/site/States";
import { PoBadge, Pill } from "@/components/site/Badges";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function resolveLine(l, p) {
  if (!p) return { ...l, error: "Produk sudah dihapus", unit: 0, max: 0 };
  if (!p.available) return { ...l, p, error: "Produk tidak aktif", unit: 0, max: 0 };
  const max = lineMax(p, l.mode, l.variant);
  if (l.mode === "po") {
    const po = p.po_info;
    if (!po?.orderable) return { ...l, p, error: `PO tidak dibuka (${po?.status_label || "-"})`, unit: po?.price || 0, max: 0 };
    return { ...l, p, unit: po.price, dp: po.dp_per_unit, max, error: l.qty > max ? `Maksimal ${max} pcs` : "" };
  }
  if (!p.ready_orderable || max < 1) return { ...l, p, error: "Stok habis", unit: p.pricing.final_price, max: 0 };
  return { ...l, p, unit: p.pricing.final_price, normal: p.pricing.price, max, error: l.qty > max ? `Stok tersisa ${max}` : "" };
}

function Line({ l }) {
  const { setQty, removeLine } = useShop();
  return (
    <div className="flex gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border bg-white" data-testid={`cart-line-${l.key}`}>
      <img src={imgUrl(l.image)} alt="" className="h-20 w-20 sm:h-24 sm:w-24 rounded-lg object-cover border shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <Link to={`/${l.mode === "po" ? "pre-order" : "produk"}/${l.slug}`} className="font-heading font-semibold leading-snug line-clamp-2 hover:underline">{l.name}</Link>
            <div className="text-xs text-neutral-500 mt-0.5 flex flex-wrap gap-1.5 items-center">
              {l.mode === "po" ? <PoBadge status={l.p?.po_info?.status} /> : <Pill cls="bg-[var(--bk-yellow)] border border-neutral-900">Ready</Pill>}
              {l.variant && <span>Varian: {l.variant}</span>}
              {l.p && <span className="font-mono">{l.p.sku}</span>}
            </div>
          </div>
          <button data-testid={`cart-remove-${l.key}`} onClick={() => removeLine(l.key)} aria-label="Hapus" className="h-9 w-9 grid place-items-center rounded-lg hover:bg-red-50 text-neutral-500 hover:text-[var(--bk-red)]"><Trash2 className="h-4 w-4" /></button>
        </div>
        {l.error && <div className="mt-1 text-xs font-semibold text-[var(--bk-red)] flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />{l.error}</div>}
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <div className="inline-flex items-center rounded-full border-2 border-neutral-900 h-9">
            <button data-testid={`cart-qty-dec-${l.key}`} onClick={() => setQty(l.key, Math.max(1, l.qty - 1))} className="w-9 h-full grid place-items-center" aria-label="Kurangi"><Minus className="h-3.5 w-3.5" /></button>
            <span className="w-8 text-center text-sm font-bold" data-testid={`cart-qty-${l.key}`}>{l.qty}</span>
            <button data-testid={`cart-qty-inc-${l.key}`} disabled={l.qty >= l.max} onClick={() => setQty(l.key, l.qty + 1)} className="w-9 h-full grid place-items-center disabled:opacity-30" aria-label="Tambah"><Plus className="h-3.5 w-3.5" /></button>
          </div>
          <div className="text-right">
            <div className="text-xs text-neutral-500">{rp(l.unit)} / pcs{l.mode === "po" && l.dp ? ` · DP ${rp(l.dp)}` : ""}</div>
            <div className="price font-bold" data-testid={`cart-subtotal-${l.key}`}>{rp(l.unit * l.qty)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SuccessDialog({ result, onClose }) {
  const s = useSettings();
  return (
    <Dialog open={!!result} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="order-success-dialog" className="max-w-md">
        <CheckCircle2 className="h-12 w-12 text-emerald-600" />
        <DialogTitle className="font-heading text-2xl">Pesanan tersimpan!</DialogTitle>
        <DialogDescription asChild>
          <div className="space-y-3 text-sm text-neutral-700">
            {result?.orders.map((o) => (
              <div key={o.order_no} className="rounded-lg border p-3 bg-neutral-50">
                <div className="text-xs text-neutral-500">{o.kind === "po" ? "Pesanan Pre-Order" : "Pesanan Ready Stock"}</div>
                <div className="font-mono font-bold text-lg text-neutral-900" data-testid={`order-number-${o.kind}`}>{o.order_no}</div>
                <div>Total: <b>{rp(o.total)}</b>{o.kind === "po" && <> · DP: <b>{rp(o.dp_total)}</b></>}</div>
                {o.payment_method === "qris" ? (
                  <Link to={`/bayar/${o.order_no}?t=${o.token}`} className="btn-ink w-full mt-2" data-testid={`order-qris-${o.kind}`}>Bayar dengan QRIS</Link>
                ) : (
                  <Link to={`/nota/${o.order_no}?t=${o.token}`} className="btn-ink w-full mt-2" data-testid={`order-nota-${o.kind}`}>Lihat Nota & Bayar Transfer</Link>
                )}
              </div>
            ))}
            <p className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-amber-900">Bayar sesuai nota (scan QRIS atau transfer bank), unggah bukti pembayaran, lalu konfirmasi ke WhatsApp. Pesanan yang belum dibayar dalam {s.payment_deadline_hours || 24} jam akan dibatalkan otomatis. <b>Status lunas setelah admin memverifikasi pembayaran.</b></p>
          </div>
        </DialogDescription>
        <a href={result?.wa_url} target="_blank" rel="noreferrer" className="btn-wa w-full" data-testid="order-open-wa-button"><MessageCircle className="h-4 w-4" />Kirim Detail Pesanan via WhatsApp</a>
        <Link to={`/lacak-pesanan?no=${result?.orders?.[0]?.order_no || ""}`} className="text-sm text-center font-semibold underline" data-testid="order-track-link">Simpan nomor pesanan & lacak statusnya di sini</Link>
      </DialogContent>
    </Dialog>
  );
}

export default function Cart() {
  useSeo("Keranjang Belanja");
  const { cart, clearCart } = useShop();
  const s = useSettings();
  const [form, setForm] = useState({ customer_name: "", phone: "", fulfillment: "pickup", address: "", notes: "", website: "", payment_method: "qris" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [result, setResult] = useState(null);
  const [dlg, setDlg] = useState(true);
  const ids = [...new Set(cart.map((l) => l.product_id))];
  const { data, isLoading, refetch } = useQuery({ queryKey: ["cart-products", ids], queryFn: () => api.get("/products/by-ids", { params: { ids: ids.join(",") } }).then((r) => r.data), enabled: ids.length > 0 });

  if (result) return <div className="container-x py-16"><Empty title="Terima kasih!" text={`Nomor pesanan: ${result.orders.map((o) => o.order_no).join(", ")}`} action={<a href={result.wa_url} target="_blank" rel="noreferrer" className="btn-wa" data-testid="cart-wa-again">Kirim Pesan WhatsApp</a>} /><SuccessDialog result={dlg ? result : null} onClose={() => setDlg(false)} /></div>;
  if (!cart.length) return <div className="container-x py-16" data-testid="cart-page"><Empty title="Keranjang masih kosong" text="Yuk cari koleksi favoritmu." action={<Link to="/katalog" className="btn-brand" data-testid="cart-browse-btn">Jelajahi Katalog</Link>} /></div>;
  if (isLoading || !data) return <Loading />;

  const map = Object.fromEntries(data.map((p) => [p.id, p]));
  const lines = cart.map((l) => resolveLine(l, map[l.product_id]));
  const ready = lines.filter((l) => l.mode === "ready");
  const po = lines.filter((l) => l.mode === "po");
  const sum = (ls, f) => ls.reduce((a, l) => a + f(l), 0);
  const readyTotal = sum(ready, (l) => l.unit * l.qty);
  const poTotal = sum(po, (l) => l.unit * l.qty);
  const dpTotal = sum(po, (l) => (l.dp || 0) * l.qty);
  const hasError = lines.some((l) => l.error);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (form.customer_name.trim().length < 2) er.customer_name = "Nama wajib diisi";
    if (!/^(\+62|62|0)8\d{7,12}$/.test(form.phone.replace(/[\s\-().]/g, ""))) er.phone = "Nomor WhatsApp tidak valid (contoh 081234567890)";
    if (form.fulfillment === "delivery" && form.address.trim().length < 10) er.address = "Alamat lengkap wajib diisi untuk pengiriman";
    setErrors(er);
    if (Object.keys(er).length || hasError) return;
    setSubmitting(true);
    setApiError("");
    try {
      const { data: r } = await api.post("/orders", { ...form, items: cart.map((l) => ({ product_id: l.product_id, variant: l.variant || null, qty: l.qty, mode: l.mode })) });
      setResult(r);
      clearCart();
    } catch (err) {
      setApiError(errMsg(err));
      refetch();
    } finally {
      setSubmitting(false);
    }
  };

  const field = "w-full h-11 rounded-lg border-2 border-neutral-300 px-3 text-sm focus:border-neutral-900 outline-none bg-white";
  return (
    <div className="container-x py-8" data-testid="cart-page">
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold">Keranjang Belanja</h1>
      <div className="mt-6 grid lg:grid-cols-[1fr_400px] gap-8">
        <div className="space-y-8">
          {ready.length > 0 && (
            <section data-testid="cart-ready-section">
              <h2 className="font-heading font-bold text-lg mb-3">Ready Stock</h2>
              <div className="space-y-3">{ready.map((l) => <Line key={l.key} l={l} />)}</div>
              <div className="text-right mt-2 text-sm">Subtotal Ready Stock: <b className="price" data-testid="cart-ready-total">{rp(readyTotal)}</b></div>
            </section>
          )}
          {po.length > 0 && (
            <section data-testid="cart-po-section">
              <h2 className="font-heading font-bold text-lg mb-1">Pre-Order</h2>
              <p className="text-xs text-neutral-600 mb-3">Pesanan PO dibuat terpisah dari Ready Stock dengan nomor pesanan sendiri dan mengikuti ketentuan DP/pelunasan.</p>
              <div className="space-y-3">{po.map((l) => <Line key={l.key} l={l} />)}</div>
              <div className="text-right mt-2 text-sm space-y-0.5">
                <div>Subtotal PO: <b className="price" data-testid="cart-po-total">{rp(poTotal)}</b></div>
                <div>Total DP: <b className="price" data-testid="cart-dp-total">{rp(dpTotal)}</b> · Sisa pelunasan: <b className="price">{rp(poTotal - dpTotal)}</b></div>
              </div>
            </section>
          )}
        </div>

        <form onSubmit={submit} className="rounded-2xl border-2 border-neutral-900 bg-white p-5 h-fit lg:sticky lg:top-28 space-y-4" data-testid="checkout-form" noValidate>
          <h2 className="font-heading font-bold text-xl">Data Pemesanan</h2>
          <div>
            <label className="text-sm font-semibold">Nama lengkap</label>
            <input data-testid="checkout-name-input" className={field} value={form.customer_name} onChange={upd("customer_name")} maxLength={80} />
            {errors.customer_name && <p className="text-xs text-[var(--bk-red)] mt-1">{errors.customer_name}</p>}
          </div>
          <div>
            <label className="text-sm font-semibold">Nomor WhatsApp</label>
            <input data-testid="checkout-phone-input" inputMode="tel" placeholder="081234567890" className={field} value={form.phone} onChange={upd("phone")} maxLength={20} />
            {errors.phone && <p className="text-xs text-[var(--bk-red)] mt-1">{errors.phone}</p>}
          </div>
          <div>
            <label className="text-sm font-semibold">Pengambilan</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {[["pickup", "Ambil di Toko"], ...(s.shipping_enabled !== false ? [["delivery", "Pengiriman"]] : [])].map(([v, l]) => (
                <button type="button" key={v} data-testid={`checkout-fulfillment-${v}`} onClick={() => setForm((f) => ({ ...f, fulfillment: v }))} className={`h-11 rounded-lg border-2 text-sm font-semibold ${form.fulfillment === v ? "border-neutral-900 bg-[var(--bk-yellow)]" : "border-neutral-300"}`}>{l}</button>
              ))}
            </div>
            {form.fulfillment === "delivery" && <p className="text-xs text-neutral-600 mt-1">{s.shipping_note}</p>}
          </div>
          <div>
            <label className="text-sm font-semibold">Metode pembayaran</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {[...(s.qris_enabled !== false ? [["qris", "QRIS"]] : []), ["transfer", "Transfer Bank"]].map(([v, l]) => (
                <button type="button" key={v} data-testid={`checkout-pay-${v}`} onClick={() => setForm((f) => ({ ...f, payment_method: v }))} className={`h-11 rounded-lg border-2 text-sm font-semibold ${(s.qris_enabled === false ? "transfer" : form.payment_method) === v ? "border-neutral-900 bg-[var(--bk-yellow)]" : "border-neutral-300"}`}>{l}</button>
              ))}
            </div>
            <p className="text-xs text-neutral-600 mt-1">Pembayaran dicek manual oleh admin setelah kamu unggah bukti.</p>
          </div>
          {form.fulfillment === "delivery" && (
            <div>
              <label className="text-sm font-semibold">Alamat pengiriman</label>
              <textarea data-testid="checkout-address-input" rows={3} className={`${field} h-auto py-2`} value={form.address} onChange={upd("address")} maxLength={400} />
              {errors.address && <p className="text-xs text-[var(--bk-red)] mt-1">{errors.address}</p>}
            </div>
          )}
          <div>
            <label className="text-sm font-semibold">Catatan (opsional)</label>
            <textarea data-testid="checkout-notes-input" rows={2} className={`${field} h-auto py-2`} value={form.notes} onChange={upd("notes")} maxLength={500} />
          </div>
          <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={upd("website")} className="hidden" aria-hidden="true" />
          {waContacts(s).length > 1 && (
            <div>
              <label className="text-sm font-semibold">Kirim pesanan ke admin</label>
              <select data-testid="checkout-wa-target" className={field} value={form.wa_target || s.whatsapp} onChange={upd("wa_target")}>
                {waContacts(s).map((c) => <option key={c.number} value={c.number}>{c.label} ({c.display})</option>)}
              </select>
            </div>
          )}
          <div className="border-t pt-4 space-y-1 text-sm">
            {ready.length > 0 && <div className="flex justify-between"><span>Total Ready Stock</span><b className="price">{rp(readyTotal)}</b></div>}
            {po.length > 0 && <div className="flex justify-between"><span>Total Pre-Order</span><b className="price">{rp(poTotal)}</b></div>}
            {po.length > 0 && <div className="flex justify-between text-neutral-600"><span>DP Pre-Order</span><span className="price">{rp(dpTotal)}</span></div>}
            <div className="flex justify-between text-lg pt-1"><span className="font-bold">Total pesanan</span><b className="price" data-testid="cart-grand-total">{rp(readyTotal + poTotal)}</b></div>
            {form.fulfillment === "delivery" && <div className="text-xs text-neutral-500">Belum termasuk ongkos kirim.</div>}
          </div>
          {hasError && <p className="text-xs text-[var(--bk-red)] font-semibold" data-testid="cart-has-error">Perbaiki item yang bermasalah sebelum memesan.</p>}
          {apiError && <p className="text-sm text-[var(--bk-red)] bg-red-50 border border-red-200 rounded-lg p-2" data-testid="checkout-api-error">{apiError}</p>}
          <button type="submit" data-testid="checkout-wa-button" disabled={submitting || hasError} className="btn-wa w-full"><MessageCircle className="h-4 w-4" />{submitting ? "Menyimpan pesanan..." : "Pesan via WhatsApp"}</button>
          <p className="text-[11.5px] text-neutral-600 leading-relaxed">Pesanan disimpan dengan nomor referensi sebelum WhatsApp dibuka. Mengirim pesan WhatsApp <b>belum berarti</b> pesanan dikonfirmasi atau dibayar.</p>
        </form>
      </div>
    </div>
  );
}
